import { useEffect, useState, type CSSProperties } from "react";
import { ApiError, type InstanceGroupSummary } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { Modal } from "../components/chrome/Modal";

const token = () => getAccessToken() ?? "";

const NEW_GROUP = "new";

/**
 * Puts one dial into one of the user's personal groups, or into a new group of its own (KEY-564).
 *
 * Built for a dial another organisation feeds you. It already sits in My Dials, but nothing there says it came
 * by feed, so accepting the feed is the moment you are most likely to want it alongside your own dials. A group
 * holds only a reference: if the feed ends the dial drops out of the group's view by itself, and returns if the
 * feed is ever restored, so there is nothing here that needs undoing later.
 */
export function AddToGroupModal({
  instanceId,
  dialName,
  onClose,
  onAdded,
}: {
  instanceId: string;
  dialName: string;
  onClose: () => void;
  /** Called with the group's name once the dial is in it. */
  onAdded: (groupName: string) => void;
}) {
  const [groups, setGroups] = useState<InstanceGroupSummary[] | null>(null);
  const [choice, setChoice] = useState(NEW_GROUP);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listMyGroups(token())
      .then((mine) => {
        if (cancelled) return;
        setGroups(mine);
        // Default to the first group it can join; a new group only when there is none.
        const open = mine.find((g) => !g.memberInstanceIds?.includes(instanceId));
        if (open) setChoice(open.id);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  async function add() {
    const trimmed = name.trim();
    if (choice === NEW_GROUP && !trimmed) {
      setErr("Give your group a name.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (choice === NEW_GROUP) {
        const created = await api.createGroup(token(), { name: trimmed, memberInstanceIds: [instanceId] });
        onAdded(created.name);
      } else {
        const joined = await api.addGroupMember(token(), choice, instanceId);
        onAdded(joined.name);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not add the dial to that group.");
      setBusy(false);
    }
  }

  return (
    <Modal title="Add to a group" subtitle={`Roll “${dialName}” up alongside your own dials.`} onClose={onClose} width={480}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
          {groups === null && <div style={{ padding: 16, textAlign: "center", color: "var(--text-faint)", fontSize: 13.5 }}>Loading…</div>}
          {(groups ?? []).map((g, index) => {
            const already = g.memberInstanceIds?.includes(instanceId) ?? false;
            return (
              <label
                key={g.id}
                style={{ ...row, borderTop: index === 0 ? "none" : row.borderTop, cursor: already ? "default" : "pointer", opacity: already ? 0.6 : 1 }}
              >
                <input type="radio" name="add-to-group" checked={choice === g.id} disabled={already || busy} onChange={() => setChoice(g.id)} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {g.name}
                </span>
                <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
                  {already ? "Already in it" : `${g.memberCount} ${g.memberCount === 1 ? "dial" : "dials"}`}
                </span>
              </label>
            );
          })}
          {groups !== null && (
            <label style={{ ...row, borderTop: groups.length > 0 ? row.borderTop : "none", cursor: "pointer" }}>
              <input type="radio" name="add-to-group" checked={choice === NEW_GROUP} disabled={busy} onChange={() => setChoice(NEW_GROUP)} />
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => setChoice(NEW_GROUP)}
                placeholder="A new group…"
                aria-label="New group name"
                disabled={busy}
                style={field}
              />
            </label>
          )}
        </div>

        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-faint)" }}>
          If the feed ends, the dial drops out of the group by itself.
        </p>

        {err && <p style={{ margin: 0, color: "var(--st-overdue)", fontSize: 13 }}>{err}</p>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="kd-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="kd-btn-primary" onClick={() => void add()} disabled={busy || groups === null}>
            Add to group
          </button>
        </div>
      </div>
    </Modal>
  );
}

const row = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  borderTop: "var(--border-w) solid var(--border)",
} satisfies CSSProperties;

const field: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "7px 10px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 14,
};
